import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import FocusedFlowPage from "../../components/FocusedFlowPage";
import { ADMIN_ROUTES } from "../../constants/routes";

const maroon = "#500000";

const reasonOptions = [
  "Edit User's Personal Account Information",
  "Edit User's Professional Account Information",
  "Edit User's general account information",
  "Admin Change",
  "Role Update",
  "Email Update",
  "Department Change",
];

const ReasonForEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [errors, setErrors] = useState({});

  // Get user data and return path from location state
  const user = location.state?.user;
  const returnTo = location.state?.returnTo;

  const handleNext = () => {
    const newErrors = {};

    if (!reason.trim()) {
      newErrors.reason = "Please select a reason for editing";
    }

    if (!comments.trim()) {
      newErrors.comments = "Please provide comments for this edit";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Navigate to edit form with reason and comments
    navigate(ADMIN_ROUTES.USER_EDIT, {
      state: {
        user,
        reason,
        comments,
        returnTo,
        step: "edit",
      },
    });
  };

  const handleCancel = () => {
    // Navigate back to the specified return path, or user profile, or user management
    if (returnTo) {
      navigate(returnTo);
    } else if (user && user.id) {
      navigate(ADMIN_ROUTES.USER_PROFILE(user.id));
    } else {
      // Fallback to user management if no user data
      navigate(ADMIN_ROUTES.USER_MANAGEMENT);
    }
  };

  return (
    <FocusedFlowPage
      title="Reason for Edit"
      subtitle={`Please provide a reason for editing ${user?.firstName || ""} ${user?.lastName || ""}'s information.`}
      onClose={handleCancel}
      maxWidth={680}
      actions={[
        <Button
          key="cancel"
          onClick={handleCancel}
          variant="outlined"
          sx={{
            borderColor: "#ccc",
            color: "#666",
            "&:hover": {
              borderColor: "#999",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Cancel
        </Button>,
        <Button
          key="next"
          onClick={handleNext}
          variant="contained"
          sx={{
            backgroundColor: maroon,
            "&:hover": {
              backgroundColor: "#4a0000",
            },
            fontWeight: 600,
            px: 3,
          }}
        >
          NEXT
        </Button>,
      ]}
    >
      <Box sx={{ maxWidth: "600px", mx: "auto" }}>
        {/* Form */}
        <Paper sx={{ p: 4, mb: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              Please select the reason for editing this User's Information.
            </Typography>

            <FormControl fullWidth error={!!errors.reason}>
              <InputLabel>Reason for Edit</InputLabel>
              <Select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                label="Reason for Edit"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: maroon,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: maroon,
                    },
                  },
                }}
              >
                {reasonOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {errors.reason && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.reason}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comments"
              placeholder="Please provide a brief reason for this edit. For example: Admin Change, Edit Email, Edit Role."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              error={!!errors.comments}
              helperText={errors.comments}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: maroon,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: maroon,
                  },
                },
              }}
            />
          </Box>
        </Paper>
      </Box>
    </FocusedFlowPage>
  );
};

export default ReasonForEdit;
